import { useState, useMemo, useEffect, useCallback } from "react";
import {
  CloseButton,
  Combobox,
  InputBase,
  useCombobox,
  Loader,
} from "@mantine/core";

type Option = string | { value: string; label: string };

interface SelectClearableProps {
  value?: string | null;
  data?: Option[];
  placeholder?: string;
  onChange?: (value: string | null) => void;
  loading?: boolean;
  style?: React.CSSProperties;
  className?: string;
  emptyMessage?: React.ReactNode;
  onEmptyClick?: () => void;
  onInputChange?: (value: string) => void;
  disabled?: boolean;
}

export function SelectClearable(props: SelectClearableProps) {
  const {
    data = [],
    value,
    placeholder,
    onChange,
    loading,
    emptyMessage,
    onEmptyClick,
    onInputChange,
    disabled = false,
  } = props;

  // If value is an object array, find the label for the current value
  const getLabelForValue = useCallback(
    (val: string | null) => {
      if (!val) return "";
      if (
        data &&
        data.length > 0 &&
        typeof data[0] === "object" &&
        data[0] !== null &&
        "value" in data[0]
      ) {
        const found = (data as { value: string; label: string }[]).find(
          (opt) => opt.value === val
        );
        return found ? found.label : val || "";
      }
      return val || "";
    },
    [data]
  );

  const [inputValue, setInputValue] = useState(() => getLabelForValue(value || ""));
  const [selectedOption, setSelectedOption] = useState(value);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Keep inputValue in sync with value prop only when selection changes
  useEffect(() => {
    if (
      value !== selectedOption ||
      (value && inputValue !== getLabelForValue(value))
    ) {
      const label = getLabelForValue(value || "");
      setInputValue(label);
      setSelectedOption(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, data, selectedOption, getLabelForValue]);

  // Filter options based on input value
  const filteredOptions = useMemo(() => {
    if (!inputValue) return data;
    if (typeof data[0] === "object" && data[0] !== null && "label" in data[0]) {
      const filtered = (data as { value: string; label: string }[]).filter(
        (item) => item.label.toLowerCase().includes(inputValue.toLowerCase())
      );

      return filtered;
    }
    return (data as string[]).filter((item) =>
      item.toLowerCase().includes(inputValue.toLowerCase())
    );
  }, [data, inputValue]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    setInputValue(newValue);
    combobox.openDropdown();

    if (onInputChange) {
      onInputChange(newValue);
    }
  };

  const handleOptionSubmit = (val: string) => {
    // If data is object array, set input to label, onChange to value
    if (typeof data[0] === "object" && data[0] !== null && "value" in data[0]) {
      const found = (data as { value: string; label: string }[]).find(
        (opt) => opt.value === val
      );
      setInputValue(found ? found.label : val);
      setSelectedOption(val);
      onChange && onChange(val);
    } else {
      setInputValue(val);
      setSelectedOption(val);
      onChange && onChange(val);
    }
    combobox.closeDropdown();
  };

  const handleClear = () => {
    setInputValue("");
    setSelectedOption(null);
    onChange && onChange("");
    combobox.closeDropdown();
  };

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={handleOptionSubmit}
    >
      <Combobox.Target>
        <InputBase
          w="100%"
          style={{ overflow: "hidden", width: "100%", ...props.style }}
          className={props.className}
          component="input"
          type="text"
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          rightSection={
            loading ? (
              <Loader size="xs" />
            ) : inputValue ? (
              <CloseButton
                size="sm"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClear}
                aria-label="Clear value"
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onChange={handleInputChange}
          rightSectionPointerEvents={inputValue ? "all" : "none"}
        />
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Options mah={200} style={{ overflowY: "auto" }}>
          {(() => {
            if (filteredOptions.length === 0) {
              return (
                <Combobox.Empty onClick={onEmptyClick}>
                  {emptyMessage || "Nothing found"}
                </Combobox.Empty>
              );
            } else if (
              typeof filteredOptions[0] === "object" &&
              filteredOptions[0] !== null &&
              "value" in filteredOptions[0]
            ) {
              return (
                filteredOptions as { value: string; label: string }[]
              ).map((item) => (
                <Combobox.Option value={item.value} key={item.value}>
                  {item.label}
                </Combobox.Option>
              ));
            } else {
              return (filteredOptions as string[]).map((item) => (
                <Combobox.Option value={item} key={item}>
                  {item}
                </Combobox.Option>
              ));
            }
          })()}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
